// HomePage.js — Revised for the donation/portfolio framing
// Changes vs previous:
//  - Removed QuickStatsBar, StatsUpgrade, Testimonials (fake stats / fake testimonials)
//  - Removed the "Get Started Today" signup-push block at the bottom
//  - Added a "How this works" block linking to /about and /about#orgs
//  - PricingTeaser already removed; nothing else changed in the analysis flow

import axios from 'axios';
import React, { useState, lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { pdpAPI } from '../services/api';

// Above-the-fold
import HeroSection from '../components/HeroSection';

import '../styles/HomePage.css';
import '../styles/HeroSection.css';

// Below-the-fold (lazy)
const ProblemSolution = lazy(() => import('../components/ProblemSolution'));
const HowItWorksRedesign = lazy(() => import('../components/HowItWorksRedesign'));
const LiveDemoPreview = lazy(() => import('../components/LiveDemoPreview'));
const FeaturesTabs = lazy(() => import('../components/FeaturesTabs'));
const FAQSection = lazy(() => import('../components/FAQSection'));
const FinalCTA = lazy(() => import('../components/FinalCTA'));

// Error cleanup helper — unchanged from the prior version
const cleanErrorMessage = (rawError, t) => {
  if (typeof rawError === 'string') {
    if (rawError.includes('scrape_error:') || rawError.includes('403 Client Error') || rawError.includes('Forbidden')) {
      const mainError = rawError.split('\n')[0];
      if (mainError.includes('403') || mainError.includes('Forbidden')) {
        const translated = t('analysis.error_403');
        if (translated === 'analysis.error_403' || !translated) {
          return "⛔ Unable to access this website.\n\nThis site blocks automated access. Please try a different product page.";
        }
        return translated;
      }
      if (mainError.includes('timeout') || mainError.includes('Timeout')) {
        const translated = t('analysis.error_timeout');
        if (translated === 'analysis.error_timeout' || !translated) {
          return "⏱️ Analysis timed out.\n\nThe website took too long to respond. Please try again.";
        }
        return translated;
      }
      const translated = t('analysis.error_scraping');
      if (translated === 'analysis.error_scraping' || !translated) {
        return "❌ Unable to analyze this page.\n\nWe couldn't access the website. Please check the URL and try again.";
      }
      return translated;
    }
    if (rawError.length > 500) {
      return rawError.substring(0, 350) + '\n\n...';
    }
    return rawError;
  }
  const translated = t('analysis.error_generic');
  if (translated === 'analysis.error_generic' || !translated) {
    return '❌ An unexpected error occurred. Please try again.';
  }
  return translated;
};

const HomePage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const {
    isAuthenticated,
    hasFreeAnalysisUsed,
    trackFreeAnalysis,
    fingerprint,
    creditsAvailable,
    hasLifetimeAccess,
    loading,
    hasCredits,
    refreshCredits
  } = useAuth();

  const [url, setUrl] = useState('');
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [error, setError] = useState(null);
  const [noCredits, setNoCredits] = useState(false);

  const currentLang = i18n.language || 'en';

  const seoTitle = currentLang === 'it'
    ? 'BetterPDP — Analizzatore PDP per AI, gratuito, sostenuto da donazioni umanitarie'
    : 'BetterPDP — AI-Ready PDP Analyzer, free, supported by humanitarian donations';

  const seoDescription = currentLang === 'it'
    ? 'Ottimizza le tue pagine prodotto per i motori AI come ChatGPT, Perplexity e Claude. Gratuito durante la beta. Le donazioni sostengono ONG che operano a Gaza e in Libano.'
    : 'Optimize your product pages for AI search engines like ChatGPT, Perplexity, and Claude. Free during beta. Donations support NGOs operating in Gaza and Lebanon.';

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setError(null);
    setNoCredits(false);

    if (isAuthenticated && loading) {
      setError(t('home.checking_account'));
      return;
    }

    if (isAuthenticated && !hasLifetimeAccess && !hasCredits) {
      setNoCredits(true);
      return;
    }

    setLoadingAnalysis(true);

    try {
      const response = await pdpAPI.analyzePDP(url);

      if (!response.success && response.code === 'NOT_A_PDP') {
        setLoadingAnalysis(false);
        const details = response.details || response.data?.details || response.error?.details;
        if (details) {
          showNotPDPError(details);
        } else {
          setError(t('analysis.not_pdp_generic_message'));
        }
        return;
      }

      if (response.success) {
        const analysisId = response.data?.analysis_id || response.data?.id || response.id;
        if (!analysisId) {
          setError(t('analysis.error_generic'));
          return;
        }

        if (!isAuthenticated) {
          trackFreeAnalysis(analysisId);
        } else {
          refreshCredits().catch(() => {});
        }

        navigate(`/analysis/${analysisId}`);
      } else {
        if (response.code === 'ALREADY_ANALYZED' && response.data?.analysis_id) {
          alert(t('analysis.already_analyzed'));
          navigate(`/analysis/${response.data.analysis_id}`);
          return;
        }
        if (response.code === 'NO_CREDITS') {
          setNoCredits(true);
          return;
        }
        setError(cleanErrorMessage(response.error || response.message, t));
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Unknown error';
      setError(cleanErrorMessage(errorMessage, t));
    } finally {
      setLoadingAnalysis(false);
    }
  };

  const showNotPDPError = (details) => {
    if (!details) {
      setError(t('analysis.not_pdp_generic_message'));
      return;
    }
    const { detected_type, suggestions } = details;
    let title = t('analysis.not_pdp_title');
    let message = t('analysis.not_pdp_generic_message');
    if (detected_type === 'category') {
      title = t('analysis.not_pdp_category_title');
      message = t('analysis.not_pdp_category_message');
    } else if (detected_type === 'listing') {
      title = t('analysis.not_pdp_listing_title');
      message = t('analysis.not_pdp_listing_message');
    }
    const suggestionsList = suggestions && suggestions.length > 0
      ? suggestions.slice(0, 3).map((s, i) => `${i + 1}. ${s}`).join('\n')
      : `1. ${t('analysis.not_pdp_suggestion_1')}\n2. ${t('analysis.not_pdp_suggestion_2')}\n3. ${t('analysis.not_pdp_suggestion_3')}`;
    setError(`${title}\n\n${message}\n\n${t('analysis.not_pdp_how_to_fix')}\n${suggestionsList}`);
  };

  const testBackendConnection = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/health`);
      console.log('Backend connection successful:', response.data);
    } catch (err) {
      console.error('Backend connection failed:', err);
    }
  };

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href="https://betterpdp.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://betterpdp.com/" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:image" content="https://betterpdp.com/web-app-manifest-512x512.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content="https://betterpdp.com/web-app-manifest-512x512.png" />
        <link rel="alternate" hrefLang="en" href="https://betterpdp.com/?lang=en" />
        <link rel="alternate" hrefLang="it" href="https://betterpdp.com/?lang=it" />
        <link rel="alternate" hrefLang="x-default" href="https://betterpdp.com/" />
      </Helmet>

      <main>
        <HeroSection
          url={url}
          setUrl={setUrl}
          loadingAnalysis={loadingAnalysis}
          error={error}
          noCredits={noCredits}
          handleSubmit={handleSubmit}
          testBackendConnection={testBackendConnection}
        />

        <Suspense fallback={<div style={{ minHeight: '400px' }} />}>
          <ProblemSolution />
          <HowItWorksRedesign />
          <LiveDemoPreview />
          <FeaturesTabs />
          <FAQSection />
          <FinalCTA />
        </Suspense>

        {/* "How this works" block — replaces the old signup-push.
            Quiet, mission-aligned, points at About and the org list. */}
        <section style={{
          maxWidth: '720px',
          margin: '60px auto',
          padding: '40px 24px',
          textAlign: 'center',
          background: 'rgba(9, 9, 11, 0.6)',
          borderRadius: '16px',
          border: '1px solid rgba(37, 99, 235, 0.15)'
        }}>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: '600',
            marginBottom: '12px',
            color: '#fafafa'
          }}>
            {t('home.homepage_footer_block_title')}
          </h2>
          <p style={{
            fontSize: '1rem',
            color: '#a1a1aa',
            marginBottom: '28px',
            lineHeight: '1.7',
            maxWidth: '560px',
            marginLeft: 'auto',
            marginRight: 'auto'
          }}>
            {t('home.homepage_footer_block_desc')}
          </p>
          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <Link
              to="/about"
              style={{
                padding: '12px 28px',
                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                color: 'white',
                borderRadius: '10px',
                fontWeight: '600',
                textDecoration: 'none'
              }}
            >
              {t('home.homepage_footer_block_cta_about')}
            </Link>
            <Link
              to="/about#orgs"
              style={{
                padding: '12px 28px',
                background: 'transparent',
                border: '1px solid #2563eb',
                color: '#93c5fd',
                borderRadius: '10px',
                fontWeight: '600',
                textDecoration: 'none'
              }}
            >
              {t('home.homepage_footer_block_cta_donate')}
            </Link>
          </div>
        </section>
      </main>
    </>
  );
};

export default HomePage;